import { useState, useEffect, useContext, useMemo } from 'react'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { useStatsigExperiment } from '@src/experiments/statsig-integration'
import { ProductContext } from '@src/quince-site-common/src/contexts/product-context/product-context-provider.helper'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { PRODUCT_TAGS_IMAGE_LIMIT } from '@src/quince-site-common/src/utils/constants'
import { filterProductTags } from '@src/quince-site-pdp/src/templates/productPage/productPage.utils'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'
import { ProductSectionType } from '@src/quince-site-pdp/src/types/productPage.types'

import { getInitialSelectedOptionsForProduct } from '../../productPurchase/ProductPurchase'
import { getProductImagesSelectedOptions } from '../productImages.helper'

import { filterApplicableTags, getColorProductMediaCombinations, getProductMedia } from './utils'

export const useProductImages = (product: Product, sectionData: TPDPSectionData) => {
  const initialSelectedOptionFromServer = useMemo(
    () => getInitialSelectedOptionsForProduct(product, sectionData),
    [product, sectionData],
  )
  const { experimentBucket: pdpVideoBucket } = useStatsigExperiment(STATSIG_EXPERIMENTS.PDP_VIDEO)
  const { selectedVariantPurchaseInfo } = useContext(ProductContext)
  const { experimentBucket: urgencyTagsBucket } = useStatsigExperiment(STATSIG_EXPERIMENTS.URGENCY_TAGS)

  const colorValueProductImagesMap = useMemo(() => getColorProductMediaCombinations(product), [product])

  const selectedOptionsFromState = pdpStateManager.getSelectedOptions()
  const selectedOptions = getProductImagesSelectedOptions(initialSelectedOptionFromServer, selectedOptionsFromState)
  const productImageSectionData = sectionData[ProductSectionType.PRODUCT_IMAGES]

  const [productMedia, setProductMedia] = useState(productImageSectionData.productMedia)

  const showPDPVideo = pdpVideoBucket === STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.PDP_VIDEO].buckets?.show
  const productTags = productHelper
    .getApplicableTags({
      product: product,
      selectedOptions,
    })
    .filter(productTag => filterProductTags(productTag, selectedVariantPurchaseInfo))
    .slice(0, PRODUCT_TAGS_IMAGE_LIMIT)

  useEffect(() => {
    let productMedia = getProductMedia(product, selectedOptions)

    if (showPDPVideo) {
      const isVideoInProductMedia = productMedia.some(media => 'video' in media)

      // If there is no video attached to the variant, push the product video at the last position.
      if (!isVideoInProductMedia && product.videos.length > 0 && product.videos[0]) {
        productMedia = [...productMedia, product.videos[0]]
      }
    } else {
      productMedia = productMedia.filter(media => 'image' in media)
    }

    setProductMedia(productMedia)
  }, [product, selectedOptions, showPDPVideo])

  const directory = sectionData.directory

  return {
    selectedOptions,
    colorValueProductImagesMap,
    productMedia,
    directory,
    productTags: filterApplicableTags(productTags, urgencyTagsBucket).applicableTags,
  }
}
